"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { usePathname } from "next/navigation";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { LogOut } from "lucide-react";

export function SignOutOverlay() {
  const [isSigningOut, setIsSigningOut] = useState(false);
  const { status } = useSession();
  const pathname = usePathname();

  useEffect(() => {
    // Listen for the custom sign-out event
    const handleSignOutStart = () => {
      setIsSigningOut(true);
    };

    // Listen for storage changes to detect sign-out completion
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "user-carts" && e.newValue === null) {
        console.log("[SignOutOverlay] Sign-out detected via storage change");
        // Keep overlay visible during redirect
      }
    };

    // Add event listeners
    window.addEventListener("user-signout", handleSignOutStart);
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("user-signout", handleSignOutStart);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [status, isSigningOut]);

  // Reset overlay state when navigating away from dashboard
  useEffect(() => {
    if (!pathname?.startsWith("/dashboard")) {
      setIsSigningOut(false);
    }
  }, [pathname]);

  // Don't show overlay if not signing out
  if (!isSigningOut) {
    return null;
  }

  return (
    <div className='fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center'>
      <div className='flex flex-col items-center gap-4 text-white'>
        <div className='relative'>
          <LoadingSpinner className='w-12 h-12' />
          <LogOut className='absolute inset-0 m-auto w-6 h-6' />
        </div>
        <div className='text-center'>
          <h2 className='text-xl font-semibold mb-2'>Signing Out</h2>
          <p className='text-white/80'>
            Clearing your session and redirecting...
          </p>
        </div>
      </div>
    </div>
  );
}
